import type {
  GlobalApiErrorCode,
  GlobalApiError,
} from '@data/global-api-errors';
import { ErrorWithDetails, isNetworkError } from '@utility/checkout-utils';
import type { ObjectKeys } from '@utility/typescript-generics';

import { TRANSLATION_KEYS } from './TranslationKeys';

type ApiErrorCodeAndMessageKey = {
  messageKey: ObjectKeys<typeof TRANSLATION_KEYS> | null;
  code: GlobalApiErrorCode | string | null;
  rawMessage: string | null;
};

const EMPTY_RETURN: ApiErrorCodeAndMessageKey = {
  messageKey: null,
  code: null,
  rawMessage: null,
};

const isErrorResponse = (error: unknown): error is GlobalApiError => {
  return Boolean(
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    error.data &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'code' in error.data
  );
};

export const getApiErrorCodeAndMessageKey = (
  error: unknown | null
): ApiErrorCodeAndMessageKey => {
  if (!error) {
    return EMPTY_RETURN;
  }

  if (isErrorResponse(error) && 'data' in error) {
    const { code } = error.data;
    const messageKey =
      TRANSLATION_KEYS[code as ObjectKeys<typeof TRANSLATION_KEYS>];

    return {
      messageKey: messageKey ?? null,
      code: code ?? null,
      rawMessage: error.data.message ?? null,
    };
  }

  if (isNetworkError(error)) {
    return {
      messageKey: TRANSLATION_KEYS.NETWORK_ERROR,
      code: error.name,
      rawMessage: error.message,
    };
  }

  if (error instanceof Error && !('data' in error)) {
    return {
      messageKey: null,
      code: error.name,
      rawMessage: error.message,
    };
  }

  if (error instanceof ErrorWithDetails) {
    return {
      messageKey: null,
      code: error.name,
      rawMessage: error.message,
    };
  }

  return EMPTY_RETURN;
};
